import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Parameter, ParameterRange } from '@shared/types';
import { Input } from '@/components/ui/input';
import { getParameterIcon } from '@/utils/parameterUtils';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';
import { Minus, Plus } from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveNum(v: ParameterRange['min']): number | undefined {
  return typeof v === 'number' ? v : undefined;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function snapToStep(value: number, step: number | undefined): number {
  if (!step || step <= 0) return value;
  return Math.round(value / step) * step;
}

/** Map a fraction [0,1] to a value within [min, max] with optional snapping. */
function fracToValue(
  frac: number,
  min: number,
  max: number,
  step?: number,
): number {
  const raw = min + frac * (max - min);
  return snapToStep(clamp(raw, min, max), step);
}

/** Map a value to a fraction [0,1] within [min, max]. */
function valueToFrac(value: number, min: number, max: number): number {
  if (max === min) return 0;
  return clamp((value - min) / (max - min), 0, 1);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function XYPadInput({
  param,
  onCommit,
}: {
  param: Parameter;
  onCommit: (value: Parameter['value']) => void;
}) {
  const axes = param.xyPadAxes;

  // Extract per-axis bounds (fall back to 0–100 if not specified).
  const xMin = resolveNum(axes?.x.min) ?? 0;
  const xMax = resolveNum(axes?.x.max) ?? 100;
  const xStep = resolveNum(axes?.x.step);
  const yMin = resolveNum(axes?.y.min) ?? 0;
  const yMax = resolveNum(axes?.y.max) ?? 100;
  const yStep = resolveNum(axes?.y.step);
  const xLabel = axes?.xLabel ?? 'X';
  const yLabel = axes?.yLabel ?? 'Y';

  // Current value — 2-element number array.
  const valueArr = Array.isArray(param.value)
    ? (param.value as number[])
    : [0, 0];
  const [localX, setLocalX] = useState<number>(valueArr[0]);
  const [localY, setLocalY] = useState<number>(valueArr[1]);

  // Keep local state in sync when the parent pushes a new value.
  const prevValueRef = useRef(param.value);
  if (prevValueRef.current !== param.value) {
    prevValueRef.current = param.value;
    setLocalX(valueArr[0]);
    setLocalY(valueArr[1]);
  }

  // Throttled commit (200 ms, same pattern as ParameterSlider).
  const lastCommitTime = useRef(0);
  const commitTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const throttledCommit = useCallback(
    (x: number, y: number) => {
      const now = Date.now();
      const doCommit = () => {
        onCommit([x, y]);
        lastCommitTime.current = Date.now();
        commitTimeout.current = null;
      };
      if (now - lastCommitTime.current >= 200) {
        doCommit();
      } else {
        if (commitTimeout.current) clearTimeout(commitTimeout.current);
        commitTimeout.current = setTimeout(
          doCommit,
          200 - (now - lastCommitTime.current),
        );
      }
    },
    [onCommit],
  );

  useEffect(() => {
    return () => {
      if (commitTimeout.current) clearTimeout(commitTimeout.current);
    };
  }, []);

  // Immediate commit (on pointer up / input blur).
  const immediateCommit = useCallback(
    (x: number, y: number) => {
      if (commitTimeout.current) {
        clearTimeout(commitTimeout.current);
        commitTimeout.current = null;
      }
      onCommit([x, y]);
      lastCommitTime.current = Date.now();
    },
    [onCommit],
  );

  // Pointer interaction on the pad.
  const padRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number, immediate: boolean) => {
      const el = padRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const fracX = (clientX - rect.left) / rect.width;
      // Y axis: top of pad = yMax, bottom = yMin (screen-Y is inverted).
      const fracY = 1 - (clientY - rect.top) / rect.height;
      const newX = fracToValue(fracX, xMin, xMax, xStep);
      const newY = fracToValue(fracY, yMin, yMax, yStep);
      setLocalX(newX);
      setLocalY(newY);
      if (immediate) {
        immediateCommit(newX, newY);
      } else {
        throttledCommit(newX, newY);
      }
    },
    [xMin, xMax, xStep, yMin, yMax, yStep, throttledCommit, immediateCommit],
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    isDragging.current = true;
    updateFromPointer(e.clientX, e.clientY, false);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    updateFromPointer(e.clientX, e.clientY, false);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    updateFromPointer(e.clientX, e.clientY, true);
  };

  // Input field handlers.
  const handleXBlur = () => {
    const clamped = snapToStep(clamp(localX, xMin, xMax), xStep);
    setLocalX(clamped);
    immediateCommit(clamped, localY);
  };

  const handleYBlur = () => {
    const clamped = snapToStep(clamp(localY, yMin, yMax), yStep);
    setLocalY(clamped);
    immediateCommit(localX, clamped);
  };

  const handleXKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const handleYKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  // Dot position as percentages within the pad.
  const dotLeft = `${valueToFrac(localX, xMin, xMax) * 100}%`;
  // Screen Y is inverted: yMax maps to top (0%), yMin maps to bottom (100%).
  const dotTop = `${(1 - valueToFrac(localY, yMin, yMax)) * 100}%`;

  // Compute an aspect ratio from the axis spans so the pad is proportional.
  const xSpan = Math.abs(xMax - xMin) || 1;
  const ySpan = Math.abs(yMax - yMin) || 1;
  // Cap aspect ratio to a sane range (0.4 – 2.5) so the pad stays usable
  // even with very asymmetric ranges.
  const rawAspect = xSpan / ySpan;
  const aspect = Math.min(2.5, Math.max(0.4, rawAspect));

  const IconComponent = getParameterIcon(param.name);

  // Format a number for display: trim unnecessary decimals.
  const fmt = (n: number) => {
    const s = n.toPrecision(6);
    return String(parseFloat(s));
  };

  return (
    <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
      <ParameterLabel
        param={param}
        IconComponent={IconComponent}
        className="pt-2"
      />
      <div className="flex flex-col gap-2">
        {/* 2D pad */}
        <div
          ref={padRef}
          className="relative w-full cursor-crosshair touch-none select-none rounded-md border border-border bg-muted/40"
          style={{ aspectRatio: String(aspect) }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {/* Grid lines (subtle, 25% increments) */}
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            preserveAspectRatio="none"
          >
            {[25, 50, 75].map((pct) => (
              <React.Fragment key={pct}>
                <line
                  x1={`${pct}%`}
                  y1="0%"
                  x2={`${pct}%`}
                  y2="100%"
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeWidth="1"
                />
                <line
                  x1="0%"
                  y1={`${pct}%`}
                  x2="100%"
                  y2={`${pct}%`}
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeWidth="1"
                />
              </React.Fragment>
            ))}
            {/* Centre crosshair */}
            <line
              x1="50%"
              y1="0%"
              x2="50%"
              y2="100%"
              stroke="currentColor"
              strokeOpacity="0.18"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <line
              x1="0%"
              y1="50%"
              x2="100%"
              y2="50%"
              stroke="currentColor"
              strokeOpacity="0.18"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
          </svg>

          {/* Dot indicator */}
          <div
            className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-primary shadow-sm"
            style={{ left: dotLeft, top: dotTop }}
          />
        </div>

        {/* Numeric inputs */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <span className="pl-1 text-[10px] text-muted-foreground">
              {xLabel}
            </span>
            <div className="flex items-center gap-1">
              <Input
                className="h-7 min-w-0 flex-1 px-2 text-center text-xs"
                value={fmt(localX)}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  if (!isNaN(n)) setLocalX(n);
                }}
                onFocus={(e) => e.target.select()}
                onBlur={handleXBlur}
                onKeyDown={handleXKey}
              />
              <div className="flex flex-col">
                <button
                  className="flex h-3.5 w-5 items-center justify-center rounded-t border border-b-0 border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                  tabIndex={-1}
                  aria-label="Increase X"
                  disabled={localX >= xMax}
                  onClick={() => {
                    const step = xStep ?? 1;
                    const next = Math.min(xMax, Math.round((localX + step) / step) * step);
                    setLocalX(next);
                    immediateCommit(next, localY);
                  }}
                >
                  <Plus className="h-2.5 w-2.5" />
                </button>
                <button
                  className="flex h-3.5 w-5 items-center justify-center rounded-b border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                  tabIndex={-1}
                  aria-label="Decrease X"
                  disabled={localX <= xMin}
                  onClick={() => {
                    const step = xStep ?? 1;
                    const next = Math.max(xMin, Math.round((localX - step) / step) * step);
                    setLocalX(next);
                    immediateCommit(next, localY);
                  }}
                >
                  <Minus className="h-2.5 w-2.5" />
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="pl-1 text-[10px] text-muted-foreground">
              {yLabel}
            </span>
            <div className="flex items-center gap-1">
              <Input
                className="h-7 min-w-0 flex-1 px-2 text-center text-xs"
                value={fmt(localY)}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  if (!isNaN(n)) setLocalY(n);
                }}
                onFocus={(e) => e.target.select()}
                onBlur={handleYBlur}
                onKeyDown={handleYKey}
              />
              <div className="flex flex-col">
                <button
                  className="flex h-3.5 w-5 items-center justify-center rounded-t border border-b-0 border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                  tabIndex={-1}
                  aria-label="Increase Y"
                  disabled={localY >= yMax}
                  onClick={() => {
                    const step = yStep ?? 1;
                    const next = Math.min(yMax, Math.round((localY + step) / step) * step);
                    setLocalY(next);
                    immediateCommit(localX, next);
                  }}
                >
                  <Plus className="h-2.5 w-2.5" />
                </button>
                <button
                  className="flex h-3.5 w-5 items-center justify-center rounded-b border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
                  tabIndex={-1}
                  aria-label="Decrease Y"
                  disabled={localY <= yMin}
                  onClick={() => {
                    const step = yStep ?? 1;
                    const next = Math.max(yMin, Math.round((localY - step) / step) * step);
                    setLocalY(next);
                    immediateCommit(localX, next);
                  }}
                >
                  <Minus className="h-2.5 w-2.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
